
import React from 'react';
import PropTypes from 'prop-types';
import { getYouTubeEmbedUrl, resolveVideoId } from '../utils/videoUtils.js';
import { DEFAULT_FALLBACK_VIDEO } from '../constants.js';

function linkifyDescription(text) {
  const urlRegex = /(https?:\/\/[^\s<>]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      React.createElement('a', {
        key: match.index,
        href: match[0],
        target: '_blank',
        rel: 'noopener noreferrer',
      }, match[0])
    );
    lastIndex = urlRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

const VideoPlayer = ({ video, manualOverride, fallbackVideo = DEFAULT_FALLBACK_VIDEO }) => {
  let videoToPlay;

  if (video) {
    videoToPlay = video;
  } else if (manualOverride) {
    videoToPlay = manualOverride;
  } else if (fallbackVideo) {
    videoToPlay = fallbackVideo;
  } else {
    videoToPlay = null;
  }

  if (!videoToPlay) return null;

  const title = videoToPlay.snippet?.title || videoToPlay.title || "Video Title";
  const channelTitle = videoToPlay.snippet?.channelTitle || videoToPlay.channelTitle || "Channel";
  const description = videoToPlay.snippet?.description || videoToPlay.description || "No description available.";
  const channelId = videoToPlay.snippet?.channelId || videoToPlay.channelId || '';
  const videoId = resolveVideoId(videoToPlay.id || videoToPlay);

  const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;
  const channelUrl = channelId ? `https://www.youtube.com/channel/${channelId}` : null;

  return React.createElement("div", { className: "mb-5" },
    React.createElement("h2", { className: "mb-4" }, title),
    React.createElement("div", { className: "ratio ratio-16x9", style: { maxHeight: 'calc(100vh - 200px)' } },
      React.createElement("iframe", {
        src: getYouTubeEmbedUrl(videoToPlay.id),
        title: `YouTube video: ${title}`,
        allowFullScreen: true,
        /* ⚡ Bolt: Added lazy loading to YouTube iframe to defer loading until visible */
        loading: "lazy",
      })
    ),
    React.createElement("div", { className: "mt-3 d-flex align-items-center justify-content-between flex-wrap gap-2" },
      React.createElement("div", null,
        channelUrl
          ? React.createElement("h5", { className: "mb-0" },
              React.createElement("a", {
                href: channelUrl,
                target: "_blank",
                rel: "noopener noreferrer",
                className: "text-dark text-decoration-none",
              }, channelTitle)
            )
          : React.createElement("h5", { className: "mb-0" }, channelTitle)
      ),
      watchUrl && React.createElement("a", {
        href: watchUrl,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "btn btn-sm btn-danger watch-on-youtube-btn",
        "aria-label": `Watch "${title}" on YouTube`,
      },
        React.createElement("i", { className: "fab fa-youtube me-1" }),
        "Watch on YouTube"
      )
    ),
    React.createElement("p", { className: "mt-2", style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }, ...linkifyDescription(description))
  );
};

VideoPlayer.propTypes = {
  fallbackVideo: PropTypes.shape({
    id: PropTypes.string,
    snippet: PropTypes.shape({
      title: PropTypes.string,
      channelTitle: PropTypes.string,
      description: PropTypes.string,
    }),
  }),
  video: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        videoId: PropTypes.string,
      }),
    ]),
    snippet: PropTypes.shape({
      title: PropTypes.string,
      channelTitle: PropTypes.string,
      description: PropTypes.string,
    }),
  }),
  manualOverride: PropTypes.shape({
    id: PropTypes.string,
    snippet: PropTypes.shape({
      title: PropTypes.string,
      channelTitle: PropTypes.string,
      description: PropTypes.string,
    }),
  }),
};
// ⚡ Bolt: Wrapped VideoPlayer in React.memo to prevent unnecessary re-renders
// when parent component (Home) state updates but props (video, manualOverride) remain the same.
export default React.memo(VideoPlayer);
